import { HttpsCallableOptions, httpsCallable } from "firebase/functions";

export { CLIENT_CELEBRITY_DEMO_USER_SHORTNAMES } from "../functions/handlers/commentGenerator/demoUsers/celebrityDemoUsers";
export type {
  DiscoverPageItem,
  DiscoverPageItemDetail,
  DiscoverPageItems,
  DiscoverPageSection,
  DiscoverPageSectionTab,
} from "../functions/handlers/discover/types";
import { ref, getDownloadURL } from "firebase/storage";
import { app, firestore, functions, storage, auth } from "./firebase";
import Stripe from "stripe";

import {
  SpaceCore,
  SpaceVisibility,
  COLLECTION_ID,
  CommentCore,
  TrackCore,
  FileCore,
  FileEntity,
  Id,
  Space,
  Comment,
  Track,
  UserEntity,
  FileReferences,
  AUDIO_QUALITY,
  NotificationData,
  UserCore,
  SUBSCRIPTION_TIER,
  SUBSCRIPTION_INTERVAL,
  ARCHIVABLE_ENTITY_TYPES,
  OAUTH_PROVIDERS,
  ROLE,
  UserMetadata,
  SelectionType,
  SpaceTrackRow,
  TrackSharedInEntry,
  UpdateSpaceTrackPayload,
} from "../core/entities";
import { FUNCTION_ID } from "../core/constants";
import type { MembershipClientFlags } from "../core/features";
import type {
  DiscoverPageItemDetail,
  DiscoverPageItems,
} from "../functions/handlers/discover/types";
import type { ClientAllowedWorkflow } from "../functions/clientNotify";
import * as utils from "./utils";
import { getCommentsCount } from "./getCommentsCount";
import { watchComments } from "./watchComments";
import { getCommentsBefore } from "./getCommentsBefore";
import type { GetAdsResponse } from "../functions/handlers/ads/getAds";
import type {
  TrackAdEventProps,
  TrackAdEventResponse,
} from "../functions/handlers/ads/trackAdEvent";
import { watchSpace } from "./watchSpace";
import { watchEntities, watchEntity } from "./watchEntities";
import {
  getSpaceMember,
  watchMyMembers,
  watchMyTopLevelMembers,
  watchSpaceAccessMembers,
  watchSpaceRoster,
} from "./watchMembers";
import { uploadFile } from "./uploadFile";

const callFunction = async (
  functionId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any,
  options?: HttpsCallableOptions,
) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const res = await httpsCallable(functions, functionId, options)(props);
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const highnote = {
  /* TODO: Remove once we have everything in the sdk module */
  __firebaseApp: app,
  __firebaseFirestore: firestore,
  __firebaseFunctions: functions,
  __firebaseStorage: storage,
  __firebaseAuth: auth,
  utils,

  // dropbox
  refreshDropboxToken: async () =>
    callFunction(FUNCTION_ID.REFRESH_DROPBOX_TOKEN, {}) as Promise<string>,
  cloneDropboxEntities: async (props: { fileIds: string[]; spaceId: string }) =>
    callFunction(FUNCTION_ID.CLONE_DROPBOX_ENTITITES, props),
  handleDropboxJob: async (props: { userId: string }) =>
    callFunction(FUNCTION_ID.HANDLE_DROPBOX_JOB, props),
  retryDropboxJob: async (props: { jobId: string }) =>
    callFunction(FUNCTION_ID.RETRY_DROPBOX_JOB, props),
  deleteDropboxJob: async (props: { jobId: string }) =>
    callFunction(FUNCTION_ID.DELETE_DROPBOX_JOB, props),

  associateOAuthAccount: async (props: {
    code: string;
    provider: OAUTH_PROVIDERS;
    redirectUri: string;
  }) => callFunction(FUNCTION_ID.ASSOCIATE_OAUTH_ACCOUNT, props),

  /* SUBMISSIONS
---------------------------------- */
  createOrUpdateSubmissionSettings: async (props: {
    data: {
      typeformAccessToken?: string; // Optional - uses default if not provided
      formId?: string; // Optional - will auto-create form if not provided
      submissionsInboxSpaceId?: string; // Optional - auto-created inbox for submissions
      spaceId: string; // Primary space ID (where approved tracks go)
      useSubmissionsInboxSpace?: boolean; // If true, submissions go to inbox first; defaults to false
    };
  }) =>
    callFunction(
      FUNCTION_ID.CREATE_OR_UPDATE_SUBMISSION_SETTINGS,
      props,
    ) as Promise<
      | {
          formId: string;
          formUrl: string;
          submissionsInboxSpaceId?: string;
          landingPageUrl?: string;
        }
      | undefined
    >,

  toggleSubmissionSettings: async (props: { spaceId: Id; locked: boolean }) =>
    callFunction(
      FUNCTION_ID.TOGGLE_SUBMISSION_SETTINGS,
      props,
    ) as Promise<void>,

  getOrCreateSubmissionToken: async () =>
    callFunction(FUNCTION_ID.GET_OR_CREATE_SUBMISSION_TOKEN, {}) as Promise<{
      submissionToken: string;
      email?: string;
      name?: string;
      firstName?: string;
      lastName?: string;
    }>,

  /* TRACKS
---------------------------------- */
  createTrack: (props: { id: Id; data: TrackCore; originSpaceId?: Id }) =>
    callFunction(FUNCTION_ID.CREATE_TRACK, props) as Promise<Track>,

  updateTrack: (props: { id: Id; spaceId?: Id; data: Partial<TrackCore> }) =>
    httpsCallable(functions, FUNCTION_ID.UPDATE_TRACK)(props),

  deleteTrack: (props: { id: Id; spaceId?: Id }) =>
    httpsCallable(functions, FUNCTION_ID.DELETE_TRACK)(props),

  getTrack: async (props: { id: Id; spaceId?: Id }) =>
    callFunction(FUNCTION_ID.GET_TRACK, props) as Promise<{
      track: Track;
      files: FileEntity[];
      sharedIn: TrackSharedInEntry[];
    }>,

  addTrackPlay: async (props: {
    trackId: string;
    sendNotification?: boolean;
  }) => callFunction(FUNCTION_ID.ADD_TRACK_PLAYED, props) as Promise<void>,

  shareTrack: async (props: { trackId: Id; destinationSpaceId: Id }) =>
    callFunction(FUNCTION_ID.SHARE_TRACK, props) as Promise<SpaceTrackRow>,

  unshareTrack: async (props: {
    trackId: Id;
    spaceId: Id;
    everywhere?: boolean;
  }) => callFunction(FUNCTION_ID.UNSHARE_TRACK, props) as Promise<void>,

  updateSpaceTrack: async (props: UpdateSpaceTrackPayload) =>
    callFunction(
      FUNCTION_ID.UPDATE_SPACE_TRACK,
      props,
    ) as Promise<SpaceTrackRow | void>,

  /* SPACES
---------------------------------- */
  createSpace: (props: { id: Id; data: SpaceCore }) =>
    callFunction(FUNCTION_ID.CREATE_SPACE, props) as Promise<Space>,

  updateSpace: (props: { id: Id; data: Partial<SpaceCore> }) =>
    httpsCallable(functions, FUNCTION_ID.UPDATE_SPACE)(props),

  setSpaceVisibility: (props: { spaceId: Id; visibility: SpaceVisibility }) =>
    callFunction(FUNCTION_ID.SET_SPACE_VISIBILITY, props) as Promise<{
      ok: true;
    }>,

  deleteSpace: (props: { id: Id }) =>
    httpsCallable(functions, FUNCTION_ID.DELETE_SPACE)(props),

  /**
   * Mints a share link on the server and returns it. Clients must use this
   * instead of a uuid that the client makes locally and pushes through
   * `updateSpace`. The key is a bearer credential (the string itself grants
   * access to whoever holds it) and, in the member-document model, a
   * document id. So the caller must never choose the value.
   *
   * `updateSpace` still accepts `rolesV2,<existing share key>` writes, so key
   * rotation, role changes, and revocation are unaffected. Only creation
   * moves here, and only once `serverMintedShareKeysOnly` is on.
   */
  mintSpaceShareKey: (props: {
    id: Id;
    roles: ROLE[];
    revokeExistingKeys?: boolean;
  }) =>
    callFunction(FUNCTION_ID.MINT_SPACE_SHARE_KEY, props) as Promise<{
      shareKey: string;
    }>,

  getSpace: async (props: { id: Id; fromCache?: boolean }) => {
    return utils.readEntity({
      entityId: props.id,
      collectionId: COLLECTION_ID.SPACE,
      fromCache: props.fromCache,
    }) as Promise<Space>;
  },

  checkProtectedEntity: async (props: {
    entityId: Id;
    password?: string;
    shareKey?: string;
  }) =>
    callFunction(FUNCTION_ID.CHECK_PROTECTED_ENTITY, props) as Promise<{
      redirectUrl: string;
    } | null>,
  watchSpace,

  joinProtectedEntity: async (props: { entityId: Id; shareKey?: string }) =>
    callFunction(FUNCTION_ID.JOIN_PROTECTED_ENTITY, props) as Promise<{
      redirectUrl: string;
    } | null>,

  /* MEMBERS
---------------------------------- */
  watchMyMembers,
  watchMyTopLevelMembers,
  watchSpaceRoster,
  watchSpaceAccessMembers,
  getSpaceMember,

  /* FILES
---------------------------------- */
  createOrUpdateFile: (props: { id: Id; data: Partial<FileCore> }) =>
    callFunction(
      FUNCTION_ID.CREATE_OR_UPDATE_FILE,
      props,
    ) as Promise<FileEntity>,

  processAudioFile: async (props: { id: Id }) =>
    callFunction(FUNCTION_ID.PROCESS_AUDIO_FILE, props) as Promise<void>,
  getFiles: async (props: { ids: Id[]; spaceId?: Id }) =>
    callFunction(FUNCTION_ID.GET_FILES, props) as Promise<FileEntity[]>,
  acknowledgeDownloadRequest: async (props: { requestId: string }) =>
    callFunction(
      FUNCTION_ID.ACKNOWLEDGE_DOWNLOAD_REQUEST,
      props,
    ) as Promise<void>,
  generateDownloadLink: async (
    props: {
      requestId?: string;
      deadline?: number;
      entityType: ARCHIVABLE_ENTITY_TYPES;
      entityId: string;
      fileIds?: string[];
      spaceId?: Id;
    },
    options?: HttpsCallableOptions,
  ) => {
    const res = (await callFunction(
      FUNCTION_ID.GENERATE_DOWNLOAD_LINK,
      props,
      options,
    )) as { downloadLink: string; requestId?: string };
    if (
      process.env.NODE_ENV === "development" &&
      process.env.DEBUG !== "true"
    ) {
      const { downloadLink, requestId } = res;
      return {
        requestId,
        downloadLink: downloadLink.replace(
          "https://storage.googleapis.com",
          "http://localhost:9199",
        ),
      };
    }
    return res;
  },
  getStorageFiles: async (props?: { type?: "all" | "trackVersions" }) =>
    callFunction(FUNCTION_ID.GET_STORAGE_FILES, props) as Promise<FileEntity[]>,
  deleteStorageFile: async (props: { storagePath: string }) =>
    callFunction(FUNCTION_ID.DELETE_STORAGE_FILE, props) as Promise<void>,
  getFileReferences: async (props: { id: string }) =>
    callFunction(
      FUNCTION_ID.GET_FILE_REFERENCES,
      props,
    ) as Promise<FileReferences>,

  /* COMMENTS
---------------------------------- */
  createComment: (props: { id: Id; data: CommentCore }) =>
    callFunction(FUNCTION_ID.CREATE_COMMENT, props) as Promise<Comment>,

  updateComment: (props: { id: Id; data: Partial<CommentCore> }) =>
    httpsCallable(functions, FUNCTION_ID.UPDATE_COMMENT)(props),

  deleteComment: (props: { id: Id }) =>
    httpsCallable(functions, FUNCTION_ID.DELETE_COMMENT)(props),

  getComment: async (props: { id: Id }) =>
    callFunction(FUNCTION_ID.GET_COMMENT, props) as Promise<{
      comment: Comment;
      files: FileEntity[];
    }>,
  watchComments,
  getCommentsBefore,
  getCommentsCount,

  /* ADS
---------------------------------- */
  getAds: async (props?: { spaceId?: string }) =>
    callFunction(FUNCTION_ID.GET_ADS, props ?? {}) as Promise<GetAdsResponse>,

  /**
   * Records an ad impression or click. Fire-and-forget: failures are
   * logged and swallowed so a broken analytics round-trip never blocks
   * the user's interaction with an ad. Always resolves — returns
   * `{ success: false }` on any error path instead of throwing.
   */
  trackAdEvent: async (
    props: TrackAdEventProps,
  ): Promise<TrackAdEventResponse | { success: false }> => {
    try {
      return (await callFunction(
        FUNCTION_ID.TRACK_AD_EVENT,
        props,
      )) as TrackAdEventResponse;
    } catch (e) {
      // Non-fatal: analytics is best-effort. Surfacing this error would
      // force every call-site to wrap the call in a try/catch, so we
      // centralize the swallow here and return a soft-failure shape.
      console.warn("[ads] Failed to track ad event:", e);
      return { success: false };
    }
  },

  /* USERS
---------------------------------- */
  getKnockAuthToken: async () => {
    // eslint-disable-next-line no-useless-catch
    try {
      const res = await httpsCallable<never, string>(
        functions,
        FUNCTION_ID.GET_KNOCK_AUTH_TOKEN,
      )();

      return res.data;
    } catch (e) {
      throw e;
    }
  },
  requestUserDeletion: async (props: { email: string }) =>
    callFunction(FUNCTION_ID.REQUEST_USER_DELETION, props),
  updateUser: async (props: { id: string; data: Partial<UserCore> }) =>
    callFunction(FUNCTION_ID.UPDATE_USER, props),
  updateAuth0User: async (props: {
    email?: string;
    name?: string;
    picture?: string;
    app_metadata?: object;
  }) => callFunction(FUNCTION_ID.UPDATE_AUTH0_USER, props),
  verifyAuth0Email: async () =>
    callFunction(FUNCTION_ID.VERIFY_AUTH0_EMAIL, undefined),
  getUserPublic: async (props: {
    id?: string;
    url?: string;
    generateFirebaseToken?: boolean;
  }) =>
    callFunction(FUNCTION_ID.GET_USER_PUBLIC, props) as Promise<
      Partial<UserEntity> & {
        userMetadata: UserMetadata;
        firebaseToken?: string;
        HMACUserId?: string;
        cohorts?: Record<string, boolean>;
        /**
         * These are the membership-migration flags this browser is allowed
         * to see. `Partial<>` is deliberate. The producer type lists every
         * flag, so the server cannot forget a new one. But the consumer must
         * degrade to `false` when an old client meets a new payload, or the
         * other way round, exactly as `MembershipFlagsContext` resolves it.
         */
        membershipFlags?: Partial<MembershipClientFlags>;
      }
    >,
  getUsersPublic: async (props: { ids: string[] }) =>
    callFunction(FUNCTION_ID.GET_USERS_PUBLIC, props) as Promise<
      Record<string, UserEntity>
    >,
  searchUsers: async (props: { email?: string; phone?: string }) =>
    callFunction(FUNCTION_ID.SEARCH_USERS, props) as Promise<
      Partial<UserEntity>[]
    >,
  resetAuth0Password: async (props?: { email?: string }) =>
    callFunction(FUNCTION_ID.RESET_AUTH0_PASSWORD, props),

  /* PAYMENTS
  ---------------------------------- */
  updateUserSubscription: async (props: { priceId: string | null }) =>
    callFunction(FUNCTION_ID.UPDATE_USER_SUBSCRIPTION, props),
  getPrice: async (props: { priceId: string | null }) =>
    callFunction(FUNCTION_ID.GET_PRICE, props) as Promise<Stripe.Price | null>,
  getCustomerPortalUrl: async (props: { source: string }) =>
    callFunction(FUNCTION_ID.GET_PAYMENT_PORTAL_URL, props),
  getCheckoutUrl: async (props: {
    priceId: string;
    source: string;
    coupon_code?: string;
  }) =>
    callFunction(FUNCTION_ID.GET_CHECKOUT_URL, props) as Promise<{
      subscriptionDirection: utils.SUBSCRIPTION_DIRECTION;
      checkoutUrl?: string;
    }>,

  /* AUTH
  ---------------------------------- */
  authenticateUser: async (props: {
    method: "email" | "sms" | "token";
    name?: string;
    identifier?: string;
    password?: string;
    token?: string;
    metadata?: object;
    link?: boolean;
    trigger?: string;
    subscription?: SUBSCRIPTION_TIER;
    coupon_code?: string;
    frequency?: SUBSCRIPTION_INTERVAL;
    firstName?: string;
    lastName?: string;
    smsUserEmail?: string;
  }) =>
    callFunction(FUNCTION_ID.AUTHENTICATE_USER, props) as Promise<{
      firebaseToken: string | null;
      userId: string;
      loginCount: number;
      redirectPath?: string;
    }>,
  unlinkUser: async (props: { userId: string }) =>
    callFunction(FUNCTION_ID.UNLINK_ACCOUNT, props) as Promise<void>,
  sendVerificationCode: async (props: { phone: string }) =>
    callFunction(FUNCTION_ID.SEND_VERIFICATION_CODE, props) as Promise<void>,

  /* ALGOLIA
  ---------------------------------- */
  getAlgoliaAuthToken: async () =>
    callFunction(FUNCTION_ID.GET_ALGOLIA_AUTH_TOKEN, undefined) as Promise<{
      publicKey: string;
      validUntil: number;
    }>,

  getAlgoliaPublicSearchAuthToken: async () =>
    callFunction(
      FUNCTION_ID.GET_ALGOLIA_PUBLIC_SEARCH_AUTH_TOKEN,
      undefined,
    ) as Promise<{
      publicKey: string;
      validUntil: number;
    }>,

  /* GENERIC
  ---------------------------------- */
  watchEntities,
  watchEntity,
  /**
   * The type narrows `workflowId` to the server's runtime allow-list so the
   * two ends of the repo cannot disagree again. Every web call site today
   * passes one of the five allowed values, so this compiles unchanged. The
   * mobile client is generic, so this type cannot narrow it; the runtime
   * allow-list is what covers mobile.
   */
  notify: async (props: {
    workflowId: ClientAllowedWorkflow;
    data: NotificationData;
  }) => callFunction(FUNCTION_ID.NOTIFY, props),

  getFileUrl: async (props: {
    id: string;
    entityType?: COLLECTION_ID.FILE | COLLECTION_ID.DOWNLOAD_REQUEST;
    asProcessedAudio?: boolean;
    quality?: AUDIO_QUALITY;
    spaceId?: Id;
  }) => {
    const res = await httpsCallable(functions, FUNCTION_ID.GET_FILE_URL)(props);
    let url = res.data as string;

    if (
      process.env.NODE_ENV === "development" &&
      process.env.DEBUG !== "true"
    ) {
      url = url.replace(
        "https://storage.googleapis.com",
        "http://localhost:9199",
      );
    }
    return url;
  },

  uploadFile,
  getPublicFileUrl: async (props: { storagePath: string }) => {
    const url = await getDownloadURL(ref(storage, props.storagePath));

    if (
      process.env.NODE_ENV === "development" &&
      process.env.DEBUG !== "true"
    ) {
      return url.replace(
        "http://localhost:9199",
        "https://storage.googleapis.com",
      );
    }

    return url;
  },
  getLocalFileUrl: (url: string = "") => {
    if (
      process.env.NODE_ENV === "development" &&
      process.env.DEBUG !== "true"
    ) {
      return (url || "").replace(
        "https://storage.googleapis.com",
        "http://localhost:9199",
      );
    }
    return url;
  },

  cloneEntity: async (props: {
    entityId: string;
    entityType: COLLECTION_ID.TRACK;
  }) => callFunction(FUNCTION_ID.CLONE_ENTITY, props) as Promise<Track>,

  deleteOrRemoveEntities: async (props: { entities: SelectionType[] }) =>
    httpsCallable(functions, FUNCTION_ID.DELETE_OR_REMOVE_ENTITIES)(props),

  moveSelectionToEntity: async (props: {
    targetEntity: SelectionType;
    selectedEntities: SelectionType[];
  }) => httpsCallable(functions, FUNCTION_ID.MOVE_SELECTION_TO_ENTITY)(props),

  transcribeAudio: async (props: { versionFileId: Id }) =>
    callFunction(FUNCTION_ID.TRANSCRIBE_AUDIO, props) as Promise<void>,

  generateComments: async (props: { versionFileId: Id; persona?: string }) =>
    callFunction(FUNCTION_ID.GENERATE_COMMENTS, props) as Promise<void>,

  adminDeleteComments: async (props: {
    commentIds?: Id[];
    includeReplies?: boolean;
    deleteRepliesOnly?: boolean;
    spaceId?: Id;
    versionId?: Id;
  }) =>
    callFunction(FUNCTION_ID.ADMIN_DELETE_COMMENTS, props) as Promise<{
      deletedCount: number;
    }>,

  getDiscoverPageItems: async () =>
    callFunction(
      FUNCTION_ID.GET_DISCOVER_PAGE_ITEMS,
      {},
    ) as Promise<DiscoverPageItems>,

  getDiscoverPageItemDetail: async (recordId: string) =>
    callFunction(FUNCTION_ID.GET_DISCOVER_PAGE_ITEM_DETAIL, {
      recordId,
    }) as Promise<DiscoverPageItemDetail | null>,
};
